import { Platform, TextInput, View, Animated, Pressable, FlatList, ScrollView } from 'react-native';
import React, { memo, useState, useContext, useEffect, useRef, useMemo, forwardRef } from 'react';
import { Text } from '@constants/Themed';
import TagInput from '@components/Global/TagInput';
import { ADDED_DATA_TO_STORE, CHANGE_DATA_TITLE, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import Checkbox from 'expo-checkbox';
import styles from '@stylesheet';
import { PanGestureHandler, State } from 'react-native-gesture-handler';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import Button from '@components/UI/Button/Button';
import apis, { refreshAchievements, saveData, uploadData } from '@api/api';
import { Field, Formik, FormikProps, useField } from 'formik';
import { ToolbarContext } from '@hooks/toolbarContext';
import * as Yup from 'yup';
import { YStack, XStack, H4, SizableText, Input, TextArea } from 'tamagui';

// Extend Yup string schema type to include minWords for TypeScript
declare module 'yup' {
  interface StringSchema {
    minWords(min: number, message: string): StringSchema;
  }
}
import { useDispatch, useSelector } from 'react-redux';
import handleSaveData from '../../../ToolBar/utils/handleSaveData';
import { useNavigation } from '@react-navigation/native';
import getId from '@api/getId';
import MemoizedUploadModal from '@components/modals/UploadModal/UploadModal';
import removeLeadingWhiteSpace from '@utils/removeLeadingWhiteSpace.js';
import { sanitizeDataTitle } from '@utils/sanitizeDataTitle';
import AnimatedLottieView from '@utils/lottieAnimation';
import LottieView from '@utils/lottieAnimation';

// Add a custom method to Yup
Yup.addMethod(Yup.string, 'minWords', function (min, message) {
  return this.test('min-words', message, function (value) {
    const { path, createError } = this;
    if (value) {
      const wordCount = value.trim().split(/\s+/).length;
      return wordCount >= min || createError({ path, message });
    }
    return true;
  });
});

interface DataHandlerOptionsTypes {
  tags: string[];
  setTags: Function;
  dataTitles: string;
  setDataTitles: Function;
  description: string;
  setDescription: Function;
}

const DataHandlerOptions = forwardRef<FormikProps<any>, DataHandlerOptionsTypes>((props, ref) => {
  const { tags, setTags, dataTitles, setDataTitles, description, setDescription } = props;
  const dataTracker = useSelector((state: DefaultRootState) => state.uploadPreview.present);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const theme = themeSelector();
  const [index, setIndex] = useState(0);
  const animation = new Animated.Value(0);
  const { optionsRef, setOptionsRef, editorContext, setEditorContext } = useContext(ToolbarContext);
  const [uploadModal, setUploadModal] = useState(false);
  const navigation = useNavigation();
  const dispatch = useDispatch();
  
  // Create a local ref to hold the Formik instance
  const formikRef = useRef<FormikProps<any>>(null);

  // Theme colors
  const colors = {
    background: theme ? '#ffffff' : '#1f2937',
    modalBackground: theme ? '#f8fafc' : '#374151',
    cardBackground: theme ? '#ffffff' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    primary: '#3b82f6',
    success: '#10b981',
    error: '#ef4444',
  };

  // Remove the problematic useEffect that was causing infinite loops
  // The props (tags, dataTitles, description) are controlled by the parent
  // and should be used directly in initialValues

  useEffect(() => {
    // Call the setter function from context, passing the ref received by forwardRef
    if (setOptionsRef && ref) { // Ensure setter and ref exist
      setOptionsRef(ref);
    } else {
      console.error("setOptionsRef or ref is not defined");
    }
  }, [setOptionsRef, ref]); // Dependencies: the setter function and the ref

  // Create and register editor context for description field
  useEffect(() => {
    const descriptionEditorContext = {
      ref: formikRef,
      isConnected: true,
      insertContent: (content: string) => {
        if (formikRef.current) {
          const currentDescription = formikRef.current.values.description || '';
          const newContent = currentDescription + (currentDescription ? '\n' : '') + content;
          formikRef.current.setFieldValue('description', newContent);
        }
      },
      setContent: (content: string) => {
        if (formikRef.current) {
          formikRef.current.setFieldValue('description', content);
        }
      },
      getContent: () => {
        return formikRef.current?.values.description || '';
      },
      appendContent: (content: string) => {
        if (formikRef.current) {
          const currentDescription = formikRef.current.values.description || '';
          const newContent = currentDescription + content;
          formikRef.current.setFieldValue('description', newContent);
        }
      },
      prependContent: (content: string) => {
        if (formikRef.current) {
          const currentDescription = formikRef.current.values.description || '';
          const newContent = content + currentDescription;
          formikRef.current.setFieldValue('description', newContent);
        }
      },
      clearContent: () => {
        if (formikRef.current) {
          formikRef.current.setFieldValue('description', '');
        }
      }
    };

    // Register the editor context
    if (setEditorContext) {
      setEditorContext(descriptionEditorContext);
    }

    // Cleanup function to unregister editor context when component unmounts
    return () => {
      if (setEditorContext) {
        setEditorContext(null);
      }
    };
  }, [setEditorContext]);

  const _handleSetIndex = (newIndex: number) => {
    setIndex(newIndex);
    Animated.spring(animation, {
      toValue: newIndex > 0 ? -1 : 0,
      useNativeDriver: true
    }).start();
  };



  const validationSchema = Yup.object().shape({
    dataTitle: Yup.string()
      .required('Please enter a title for your data model')
      .min(3, 'Title must be at least 3 characters')
      .max(100, 'Title must be less than 100 characters')
      .matches(
        /^[a-zA-Z0-9\s\-'.]+$/,
        'Title can only contain letters, numbers, spaces, hyphens, apostrophes, and periods'
      ),
    tags: Yup.array()
      .of(Yup.string().required('Tags are required'))
      .min(1, 'At least one tag is required')
      .max(10, 'Maximum 10 tags allowed'),
    description: Yup.string()
      .minWords(3, "Description must be at least 3 words")
      .required("Entering a description will help others understand your data model")
      .max(1000, 'Description must be less than 1000 characters')
  });

  // Create a stable key that changes when data actually changes
  const formKey = useMemo(() => {
    return `${dataTitles}-${JSON.stringify(tags)}-${description}`;
  }, [dataTitles, tags, description]);

  return (
    <YStack 
      flex={1} 
      borderRadius="$3"
      gap="$4"
      shadowColor="#000"
      shadowOffset={{ width: 0, height: 2 }}
      shadowOpacity={0.1}
      shadowRadius={4}
      elevation={3}
    >
      <MemoizedUploadModal status={.5} visible={uploadModal} setVisible={setUploadModal} />

      <Formik
        innerRef={(instance) => {
          // Set both the local ref and the forwarded ref
          formikRef.current = instance;
          if (typeof ref === 'function') {
            ref(instance);
          } else if (ref) {
            (ref as React.MutableRefObject<FormikProps<any>>).current = instance;
          }
        }}
        validationSchema={validationSchema}
        validateOnMount
        enableReinitialize={true}
        key={formKey}
        validateOnChange
        initialValues={{ dataTitle: dataTitles, tags, description }}
        onSubmit={async (values) => {
          try {
            if (dataTracker.isUpload) {
              const res = await handleSaveData(dataTracker.storedFile, dataTracker.mods.remove);
              if (!res.errors) {
                const uploadResult = await uploadData({ 
                  id: currentCompany ? currentCompany.id : getId(), 
                  data: { 
                    dataTitle: values.dataTitle, 
                    dataTags: values.tags, 
                    dataContent: res, 
                    dataSchema: dataTracker.dataHeld 
                  } 
                }) as any;
                if (uploadResult?.success) {
                  dispatch({ type: ADDED_DATA_TO_STORE });
                  dispatch({ type: HIDE_LOADING_MODAL });
                  alert('Upload successful');
                }
              } else {
                alert('An error occurred during file processing');
                dispatch({ type: HIDE_LOADING_MODAL });
                setUploadModal(false);
                return;
              }
            } else if (dataTracker.schemaId && dataTracker.schemaId !== null) {
              await apis.replaceSchema({ 
                id: dataTracker.schemaId.id, 
                data: { 
                  description: values.description, 
                  dataTitle: values.dataTitle, 
                  dataTags: values.tags, 
                  dataSchema: dataTracker.dataHeld 
                } 
              });
              dispatch({ type: HIDE_LOADING_MODAL });
            } else {
              const saveResult = await saveData({ 
                id: currentCompany ? currentCompany.id : getId(), 
                data: { 
                  description: values.description, 
                  dataTitle: values.dataTitle, 
                  dataTags: values.tags, 
                  dataContent: null, 
                  dataSchema: dataTracker.dataHeld 
                } 
              });
              if (saveResult?.success) {
                dispatch({ type: ADDED_DATA_TO_STORE });
                dispatch({ type: HIDE_LOADING_MODAL });
              } else {
                dispatch({ type: HIDE_LOADING_MODAL });
              }
            }
          } catch (error) {
            console.error('Error submitting data:', error);
            dispatch({ type: HIDE_LOADING_MODAL });
            alert('An unexpected error occurred');
          }
        }}>
        {({ handleChange, handleBlur, handleSubmit, values, errors, validateForm, isSubmitting }) => (
          <ScrollView 
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ flexGrow: 1 }}
          >
            {Platform.OS === 'web' ? (
              // Web layout: Two columns side by side
              <XStack gap="$4" flex={1}>
                {/* Left Column: Title and Tags */}
                <YStack gap="$4" flex={1}>
                  {/* Data Title Section */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600" size="$4">
                      Data Name *
                    </SizableText>
                    <Field name="dataTitle">
                      {(props) => {
                        const [field, meta, helpers] = useField(props.field);
                        const { value } = field;
                        return (
                          <YStack gap="$2">
                            <Input
                              placeholder="What would you like to call your data?"
                              value={value || ''}
                              onChangeText={(text) => {
                                const sanitized = sanitizeDataTitle(text);
                                helpers.setValue(sanitized);
                              }}
                              onBlur={handleBlur('dataTitle')}
                              backgroundColor={colors.modalBackground}
                              borderColor={meta.error ? colors.error : colors.border}
                              color={colors.text}
                              maxLength={100}
                              testID="data-title-input"
                            />
                            <SizableText color={colors.textSecondary} size="$2">
                              {(value?.length || 0)}/100 characters
                            </SizableText>
                            {meta.error && (
                              <Animatable.Text
                                animation="slideInLeft"
                                duration={300}
                                style={{ color: colors.error, fontSize: 14 }}
                              >
                                {meta.error}
                              </Animatable.Text>
                            )}
                          </YStack>
                        );
                      }}
                    </Field>
                  </YStack>

                  {/* Tags Section */}
                  <YStack gap="$2" flex={1}>
                    <SizableText color={colors.text} fontWeight="600" size="$4">
                      Tags *
                    </SizableText>
                    <Field name="tags">
                      {(props) => {
                        const [field, meta, helpers] = useField(props.field);
                        const { value } = field;
                        return (
                          <YStack gap="$2">
                            <View
                              style={{
                                backgroundColor: colors.modalBackground,
                                borderRadius: 8,
                                borderWidth: 1,
                                borderColor: meta.error ? colors.error : colors.border,
                                padding: 8,
                              }}
                            >
                              <TagInput
                                placeHolder="Add data tags, separated by space"
                                tags={value || []}
                                setTags={(v) => {
                                  helpers.setValue(v);
                                }}
                                enabled={true}
                              />
                            </View>
                            <SizableText color={colors.textSecondary} size="$2">
                              {(value?.length || 0)}/10 tags
                            </SizableText>
                            {meta.error && (
                              <Animatable.Text
                                animation="slideInUp"
                                duration={300}
                                style={{ color: colors.error, fontSize: 14 }}
                              >
                                {meta.error}
                              </Animatable.Text>
                            )}
                          </YStack>
                        );
                      }}
                    </Field>
                  </YStack>
                </YStack>

                {/* Right Column: Description */}
                <YStack gap="$2" flex={1}>
                  <SizableText color={colors.text} fontWeight="600" size="$4">
                    Data Description *
                    {editorContext?.isConnected && (
                      <SizableText color={colors.success} size="$3"> (✓ Editor Connected)</SizableText>
                    )}
                  </SizableText>
                  <Field name="description">
                    {(props) => {
                      const [field, meta, helpers] = useField(props.field);
                      const { value } = field;
                      return (
                        <YStack gap="$2" flex={1}>
                          <TextArea
                            placeholder="Describe your data - content from HelpSidePanel will appear here"
                            value={value || ''}
                            onChangeText={(text) => {
                              helpers.setValue(text);
                            }}
                            onBlur={handleBlur('description')}
                            backgroundColor={colors.modalBackground}
                            borderColor={meta.error ? colors.error : colors.border}
                            color={colors.text}
                            minHeight={120}
                            maxLength={1000}
                            textAlignVertical="top"
                            testID="data-description-input"
                            flex={1}
                          />
                          <SizableText color={colors.textSecondary} size="$2">
                            {(value?.length || 0)}/1000 characters
                          </SizableText>
                          {meta.error && (
                            <Animatable.Text
                              animation="slideInRight"
                              duration={300}
                              style={{ color: colors.error, fontSize: 14 }}
                            >
                              {meta.error}
                            </Animatable.Text>
                          )}
                        </YStack>
                      );
                    }}
                  </Field>
                </YStack>
              </XStack>
            ) : (
              // Mobile layout: Single column stacked vertically
              <YStack gap="$4" flex={1}>
                {/* Data Title Section */}
                <YStack gap="$2">
                  <SizableText color={colors.text} fontWeight="600" size="$4">
                    Data Name *
                  </SizableText>
                  <Field name="dataTitle">
                    {(props) => {
                      const [field, meta, helpers] = useField(props.field);
                      const { value } = field;
                      return (
                        <YStack gap="$2">
                          <Input
                            placeholder="What would you like to call your data?"
                            value={value || ''}
                            onChangeText={(text) => {
                              const sanitized = sanitizeDataTitle(text);
                              helpers.setValue(sanitized);
                            }}
                            onBlur={handleBlur('dataTitle')}
                            backgroundColor={colors.modalBackground}
                            borderColor={meta.error ? colors.error : colors.border}
                            color={colors.text}
                            maxLength={100}
                            testID="data-title-input"
                          />
                          <SizableText color={colors.textSecondary} size="$2">
                            {(value?.length || 0)}/100 characters
                          </SizableText>
                          {meta.error && (
                            <Animatable.Text
                              animation="slideInLeft"
                              duration={300}
                              style={{ color: colors.error, fontSize: 14 }}
                            >
                              {meta.error}
                            </Animatable.Text>
                          )}
                        </YStack>
                      );
                    }}
                  </Field>
                </YStack>

                  {/* Tags Section */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600" size="$4">
                      Tags *
                    </SizableText>
                    <Field name="tags">
                      {(props) => {
                        const [field, meta, helpers] = useField(props.field);
                        const { value } = field;
                        return (
                          <YStack gap="$2">
                            <View
                              style={{
                                backgroundColor: colors.modalBackground,
                                borderRadius: 8,
                                borderWidth: 1,
                                borderColor: meta.error ? colors.error : colors.border,
                                padding: 8,
                              }}
                            >
                              <TagInput
                                placeHolder="Add data tags, separated by space"
                                tags={value || []}
                                setTags={(v) => {
                                  helpers.setValue(v);
                                }}
                                enabled={true}
                              />
                            </View>
                            <SizableText color={colors.textSecondary} size="$2">
                              {(value?.length || 0)}/10 tags
                            </SizableText>
                            {meta.error && (
                              <Animatable.Text
                                animation="slideInUp"
                                duration={300}
                                style={{ color: colors.error, fontSize: 14 }}
                              >
                                {meta.error}
                              </Animatable.Text>
                            )}
                          </YStack>
                        );
                      }}
                    </Field>
                  </YStack>

                  {/* Description Section */}
                  <YStack gap="$2" flex={1}>
                    <SizableText color={colors.text} fontWeight="600" size="$4">
                      Data Description *
                      {editorContext?.isConnected && (
                        <SizableText color={colors.success} size="$3"> (✓ Editor Connected)</SizableText>
                      )}
                    </SizableText>
                    <Field name="description">
                      {(props) => {
                        const [field, meta, helpers] = useField(props.field);
                        const { value } = field;
                        return (
                          <YStack gap="$2" flex={1}>
                            <TextArea
                              placeholder="Describe your data - content from HelpSidePanel will appear here"
                              value={value || ''}
                              onChangeText={(text) => {
                                helpers.setValue(text);
                              }}
                              onBlur={handleBlur('description')}
                              backgroundColor={colors.modalBackground}
                              borderColor={meta.error ? colors.error : colors.border}
                              color={colors.text}
                              minHeight={120}
                              maxLength={1000}
                              textAlignVertical="top"
                              testID="data-description-input"
                            />
                            <SizableText color={colors.textSecondary} size="$2">
                              {(value?.length || 0)}/1000 characters
                            </SizableText>
                            {meta.error && (
                              <Animatable.Text
                                animation="slideInRight"
                                duration={300}
                                style={{ color: colors.error, fontSize: 14 }}
                              >
                                {meta.error}
                              </Animatable.Text>
                            )}
                          </YStack>
                        );
                      }}
                    </Field>
                  </YStack>

                  {/* Action Buttons - Only show on non-web platforms */}
                <XStack gap="$3" paddingTop="$4" borderTopWidth={1} borderTopColor={colors.border}>
                  <Pressable
                    onPress={() => _handleSetIndex(0)}
                    style={{
                      flex: 1,
                      backgroundColor: index <= 0 ? colors.primary : colors.textSecondary,
                      borderRadius: 8,
                      padding: 12,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <XStack alignItems="center" gap="$2">
                      <Ionicons name="settings" size={16} color="white" />
                      <SizableText color="white" fontWeight="500">Options</SizableText>
                    </XStack>
                  </Pressable>
                  
                  <Pressable
                    onPress={() => _handleSetIndex(1)}
                    style={{
                      flex: 1,
                      backgroundColor: index > 0 ? colors.primary : colors.textSecondary,
                      borderRadius: 8,
                      padding: 12,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <XStack alignItems="center" gap="$2">
                      <LottieView 
                        source={require("@assets/animations/automation_robot.json")} 
                        style={{ width: 20, height: 20 }} 
                        autoPlay 
                        loop 
                      />
                      <SizableText color="white" fontWeight="500">AI</SizableText>
                    </XStack>
                  </Pressable>
                  </XStack>
                </YStack>
            )}
          </ScrollView>
        )}
      </Formik>
    </YStack>
  );
});

export default memo(DataHandlerOptions);